import { useState } from 'react';
import { Search, X } from 'lucide-react';
import Button from '../../components/Button';

interface BasePickerConfig<T> {
    // Display configuration
    modalTitle: string;
    searchPlaceholder: string;
    emptyStateMessage: string;
    noSelectionMessage: string;

    // Item configuration
    getItemId: (item: T) => string;
    getItemDisplayName: (item: T) => string;
    renderItemContent?: (item: T) => React.ReactNode;

    // Usage tracking (optional)
    usageMap?: Record<string, string[]>; // Maps item ID to journey day IDs where it's used
}

interface BasePickerProps<T> {
    value: string;
    onChange: (item: T | null) => void;
    items: T[];
    config: BasePickerConfig<T>;
    label?: string;
    required?: boolean;
    className?: string;
}

export const BasePicker = <T,>({
    value,
    onChange,
    items,
    config,
    label,
    required = false,
    className = ""
}: BasePickerProps<T>) => {
    const [showModal, setShowModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const handleSelect = (item: T) => {
        onChange(item);
        setShowModal(false);
        setSearchTerm('');
    };

    const handleClear = () => {
        onChange(null);
        setSearchTerm('');
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSearchTerm('');
    };

    const filteredItems = items.filter(item => {
        const searchLower = searchTerm.toLowerCase();
        const displayName = config.getItemDisplayName(item).toLowerCase();
        const id = config.getItemId(item).toLowerCase();
        return displayName.includes(searchLower) || id.includes(searchLower);
    });

    const selectedItem = items.find(item => config.getItemId(item) === value);

    const renderDefaultItemContent = (item: T) => {
        const itemId = config.getItemId(item);
        const usageInfo = config.usageMap?.[itemId];

        return (
            <>
                <div className="font-medium text-gray-900">
                    {config.getItemDisplayName(item)}
                </div>
                <div className="text-sm text-gray-500 mt-1 break-all">
                    ID: {itemId}
                </div>
                {usageInfo && usageInfo.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                        <span className="text-xs text-gray-600">Used in:</span>
                        {usageInfo.map((dayId) => {
                            const dayNumber = dayId.match(/day_(\d+)/)?.[1] || dayId;
                            return (
                                <span
                                    key={dayId}
                                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800"
                                >
                                    Day {dayNumber}
                                </span>
                            );
                        })}
                    </div>
                )}
            </>
        );
    };

    return (
        <div className={className}>
            {/* Label */}
            {label && (
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    {label} {required && <span className="text-red-500">*</span>}
                </label>
            )}

            {/* Display Selected Value */}
            <div className="flex gap-2">
                <div className="flex-1 min-w-0 break-words px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm">
                    {value ? (
                        <span className="text-gray-900">
                            {selectedItem ? config.getItemDisplayName(selectedItem) : value}
                        </span>
                    ) : (
                        <span className="text-gray-400">{config.noSelectionMessage}</span>
                    )}
                </div>

                <Button
                    type="button"
                    onClick={() => setShowModal(true)}
                    variant="primary"
                    size="sm"
                    icon={<Search size={14} />}
                >
                    Select
                </Button>

                {value && (
                    <Button
                        type="button"
                        onClick={handleClear}
                        variant="outline"
                        size="sm"
                        icon={<X size={14} />}
                    />
                )}
            </div>

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-gray-600/50 overflow-y-auto h-full w-full z-[60] px-4 py-6 sm:py-20">
                    <div className="relative mx-auto p-0 border w-full max-w-2xl shadow-lg rounded-md bg-white">
                        {/* Header */}
                        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
                            <h3 className="text-lg font-medium text-gray-900">
                                {config.modalTitle}
                            </h3>
                            <Button
                                onClick={handleCloseModal}
                                variant="ghost"
                                icon={<X size={24} />}
                            />
                        </div>

                        {/* Search */}
                        <div className="p-4 border-b border-gray-200">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder={config.searchPlaceholder}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    autoFocus
                                />
                            </div>
                        </div>

                        {/* List */}
                        <div className="max-h-96 overflow-y-auto">
                            {filteredItems.length === 0 ? (
                                <div className="p-8 text-center text-gray-500">
                                    {items.length === 0 ? config.emptyStateMessage : 'No items found'}
                                </div>
                            ) : (
                                <ul className="divide-y divide-gray-200">
                                    {filteredItems.map((item) => {
                                        const itemId = config.getItemId(item);
                                        return (
                                            <li key={itemId}>
                                                <button
                                                    type="button"
                                                    onClick={() => handleSelect(item)}
                                                    className={`w-full text-left px-4 sm:px-6 py-4 hover:bg-gray-50 transition-colors ${value === itemId ? 'bg-blue-50' : ''
                                                        }`}
                                                >
                                                    {config.renderItemContent
                                                        ? (
                                                            <>
                                                                {config.renderItemContent(item)}
                                                                {config.usageMap?.[itemId] && config.usageMap[itemId].length > 0 && (
                                                                    <div className="mt-2 flex flex-wrap gap-1">
                                                                        <span className="text-xs text-gray-600">Used in:</span>
                                                                        {config.usageMap[itemId].map((dayId) => {
                                                                            const dayNumber = dayId.match(/day_(\d+)/)?.[1] || dayId;
                                                                            return (
                                                                                <span
                                                                                    key={dayId}
                                                                                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800"
                                                                                >
                                                                                    Day {dayNumber}
                                                                                </span>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                )}
                                                            </>
                                                        )
                                                        : renderDefaultItemContent(item)
                                                    }
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

